/**
 * API Configuration
 * Central configuration for all API endpoints
 */

export const API_BASE_URL = 'https://www.homecureexpress.com/api';

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    SEND_OTP: '/send-otp/',
    VERIFY_OTP: '/verify-otp/',
    SIGNUP: '/signup/',
    LOGIN: '/login/',
    FORGOT_PASSWORD: '/forgot-password/',
    PROFILE: '/profile/',
    UPDATE_PROFILE: '/profile/update/',
  },

  // Address Management
  ADDRESSES: {
    LIST: '/addresses/',
    ADD: '/addresses/add/',
    EDIT: (id) => `/addresses/${id}/edit/`,
    DELETE: (id) => `/addresses/${id}/delete/`,
  },

  // Medicines
  MEDICINES: {
    LIST: '/medicines/',
    DETAIL: (id) => `/medicines/${id}/`,
    SEARCH: '/medicines/search/',
  },

  // Prescriptions
  PRESCRIPTIONS: {
    UPLOAD: '/prescription/upload/',
    LIST: '/prescriptions/',
    DELETE: (id) => `/prescriptions/${id}/delete/`,
  },

  // Orders
  ORDERS: {
    PLACE: '/place-order/',
    CHECKOUT: '/checkout/',
    DETAIL: (id) => `/orders/${id}/`,
    CANCEL: (id) => `/orders/${id}/cancel/`,
    HISTORY: '/orders/history/',
    GENERATE_INVOICE: (id) => `/orders/${id}/generate-invoice/`,
    DOWNLOAD_INVOICE: (id) => `/orders/${id}/download-invoice/`,
  },

  // Medical Tests
  MEDICAL_TESTS: {
    LIST: '/medical-tests/',
    DETAIL: (id) => `/medical-tests/${id}/`,
    PACKAGES: '/medical-test-packages/',
    PACKAGE_DETAIL: (id) => `/medical-test-packages/${id}/`,
    CHECK_DELIVERY: '/medical-tests/check-delivery/',
    BOOK: '/medical-tests/book/',
    APPOINTMENTS: '/medical-tests/appointments/',
    CANCEL: (id) => `/medical-tests/appointments/${id}/cancel/`,
    RESCHEDULE: (id) => `/medical-tests/appointments/${id}/reschedule/`,
    HISTORY: '/medical-tests/appointments/history/',
    GENERATE_INVOICE: (id) => `/medical-tests/appointments/${id}/generate-invoice/`,
    DOWNLOAD_INVOICE: (id) => `/medical-tests/appointments/${id}/download-invoice/`,
  },

  // Paramedical Services
  PARAMEDICAL: {
    LIST: '/paramedical-services/',
    DETAIL: (id) => `/paramedical-services/${id}/`,
    BOOK: '/paramedical-services/book/',
    APPOINTMENTS: '/paramedical-services/appointments/',
    RESCHEDULE: (id) => `/paramedical-services/appointments/${id}/reschedule/`,
    CANCEL: (id) => `/paramedical-services/appointments/${id}/cancel/`,
    HISTORY: '/paramedical-services/appointments/history/',
    GENERATE_INVOICE: (id) => `/paramedical-services/appointments/${id}/generate-invoice/`,
    DOWNLOAD_INVOICE: (id) => `/paramedical-services/appointments/${id}/download-invoice/`,
  },

  // Doctor Appointments
  DOCTORS: {
    LIST: '/doctors/',
    DETAIL: (id) => `/doctors/${id}/`,
    SLOTS: (id) => `/slots/${id}/`,
    BOOK: '/book-appointment/', // Legacy endpoint (defaults to priority)
    BOOK_NORMAL: '/appointments/book-normal/', // Queue-based hospital visit
    BOOK_PRIORITY: '/appointments/book-priority/', // Time slot hospital visit
    APPOINTMENTS: '/appointments/',
    RESCHEDULE: (id) => `/appointments/${id}/reschedule/`,
    CANCEL: (id) => `/appointments/${id}/cancel/`,
    HISTORY: '/appointments/history/',
    GENERATE_INVOICE: (id) => `/appointments/${id}/generate-invoice/`,
    DOWNLOAD_INVOICE: (id) => `/appointments/${id}/download-invoice/`,
    DOWNLOAD_PDF: (id) => `/download-pdf/${id}/`,
  },

  // Online Consultations
  CONSULTATIONS: {
    ONLINE_DOCTORS: '/online-doctors/',
    CONSULTATIONS: '/consultations/',
    START_CHAT: '/consultation/start-chat/',
    VIDEO_SLOTS: (id) => `/consultation/video/slots/${id}/`,
    BOOK_VIDEO: '/consultation/video/book/',
    VIDEO_OFFER_INFO: '/consultation/video/offer-info/',
    ACTIVE_VIDEO_OFFERS: '/consultation/video/offers/',
    MESSAGES: (id) => `/consultation/${id}/messages/`,
    SEND_MESSAGE: (id) => `/consultation/${id}/send-message/`,
    INFO: (id) => `/consultation/${id}/info/`,
    END: (id) => `/consultation/${id}/end/`,
    RESCHEDULE: (id) => `/consultation/${id}/reschedule/`,
    CANCEL: (id) => `/consultation/${id}/cancel/`,
    MARK_READ: (id) => `/consultation/${id}/mark-read/`,
    HISTORY: '/consultations/history/',
    GENERATE_INVOICE: (id) => `/consultations/${id}/generate-invoice/`,
    DOWNLOAD_INVOICE: (id) => `/consultations/${id}/download-invoice/`,
    PRESCRIPTION: (id) => `/consultations/${id}/prescription/`,
    DOWNLOAD_PRESCRIPTION: (id) => `/consultations/${id}/prescription/download/`,
  },

  // Payment
  PAYMENT: {
    CREATE_ORDER: '/payment/create-order/',
    STATUS: (razorpayOrderId) => `/payment/status/${razorpayOrderId}/`,
  },

  // Cart
  CART: {
    GET: '/cart/',
    COUNT: '/cart/count/',
    ADD_MEDICINE: '/cart/add-medicine/',
    ADD_MEDICAL_TEST: '/cart/add-medical-test/',
    ADD_PARAMEDICAL_SERVICE: '/cart/add-paramedical-service/',
    UPDATE_QUANTITY: '/cart/update-quantity/',
    REMOVE_ITEM: '/cart/remove-item/',
    CLEAR: '/cart/clear/',
  },

  // Notifications
  NOTIFICATIONS: {
    LIST: '/notifications/',
    SEND: '/notifications/send/',
    UNREAD_COUNT: '/notifications/unread-count/',
    MARK_READ: (id) => `/notifications/${id}/mark-read/`,
    MARK_ALL_READ: '/notifications/mark-all-read/',
    PREFERENCES: '/notifications/preferences/',
    UPDATE_PREFERENCES: '/notifications/preferences/',
    STATUS: '/notifications/status/',
  },

  // Suggestions
  SUGGESTIONS: {
    GENERAL: '/suggestions/',
    DOCTORS_FOR_MEDICINE: (id) => `/suggestions/doctors-for-medicine/${id}/`,
    MEDICINES_FOR_DOCTOR: (id) => `/suggestions/medicines-for-doctor/${id}/`,
    SERVICES_FOR_DOCTOR: (id) => `/suggestions/services-for-doctor/${id}/`,
    TESTS_FOR_DOCTOR: (id) => `/suggestions/tests-for-doctor/${id}/`,
    RELATED_MEDICINES: (id) => `/suggestions/related-medicines/${id}/`,
    RELATED_SERVICES: (id) => `/suggestions/related-services/${id}/`,
    FOR_PARAMEDICAL_SERVICE: (id) => `/suggestions/for-paramedical-service/${id}/`,
    RELATED_TESTS: (id) => `/suggestions/related-tests/${id}/`,
    PACKAGES_FOR_TEST: (id) => `/suggestions/packages-for-test/${id}/`,
    RELATED_PACKAGES: (id) => `/suggestions/related-test-packages/${id}/`,
  },

  // Terms & Conditions
  TERMS: {
    LIST: '/terms-conditions/',
    CURRENT: '/terms/current/',
    SERVICE_AGREEMENT_CURRENT: '/service-agreement/current/',
    VERSION: (version) => `/terms/version/${version}/`,
    ACCEPT: '/terms-conditions/accept/',
    ACCEPT_TERMS: '/terms/accept/',
    ACCEPT_AGREEMENT: '/service-agreement/accept/',
    ACCEPT_BOTH: '/accept-both/',
    ACCEPTANCE_STATUS: '/acceptance/status/',
    ACCEPTANCE_HISTORY: '/acceptance/history/',
  },
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection.',
  GENERIC_ERROR: 'An error occurred. Please try again.',
  UNAUTHORIZED: 'Session expired. Please login again.',
  VALIDATION_ERROR: 'Please check your input and try again.',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  OTP_SENT: 'OTP sent successfully',
  OTP_VERIFIED: 'OTP verified successfully',
  LOGIN_SUCCESS: 'Login successful',
  SIGNUP_SUCCESS: 'Registration successful',
  PROFILE_UPDATED: 'Profile updated successfully',
  ADDRESS_ADDED: 'Address added successfully',
  ADDRESS_UPDATED: 'Address updated successfully',
  ADDRESS_DELETED: 'Address deleted successfully',
  ORDER_PLACED: 'Order placed successfully',
  BOOKING_SUCCESS: 'Booking confirmed successfully',
  PAYMENT_SUCCESS: 'Payment completed successfully',
  MESSAGE_SENT: 'Message sent successfully',
};

// Cache durations (in milliseconds)
export const CACHE_DURATION = {
  MEDICINES: 15 * 60 * 1000, // 15 minutes
  DOCTORS: 10 * 60 * 1000, // 10 minutes
  SERVICES: 10 * 60 * 1000, // 10 minutes
};

// Business Rules
export const BUSINESS_RULES = {
  MAX_SERVICE_RADIUS_KM: 30,
  DELIVERY_CHARGE_PER_KM: 3,

  APPOINTMENT_SLOTS: {
    DURATION_MINUTES: 10,
    DOCTOR_HOURS: { start: '11:00 AM', end: '4:00 PM' },
    VIDEO_HOURS: { start: '11:00 AM', end: '5:00 PM' },
    CHAT_HOURS: { start: '9 AM', end: '10 PM' },
    MAX_ADVANCE_DAYS: null, // No limit - can book any future date
    SAME_DAY_BUFFER_HOURS: 2, // Must book at least 2 hours in advance for same-day
  },

  PAGINATION: {
    DEFAULT_LIMIT: 20,
    MAX_LIMIT: 100,
  },
};
